import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Camera, Download, IdCard, Loader2, Save, Package, Sparkles,
  Plus, Search, X, Undo2,
} from 'lucide-react';
import InServiceTrainingCard from '../../components/inservice/InServiceTrainingCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function HRDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [available, setAvailable] = useState([]);
  const [availSearch, setAvailSearch] = useState('');
  const [availLoading, setAvailLoading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [d, a] = await Promise.all([
        axios.get(`${API_URL}/api/hr/employees/${userId}`, { headers: authHeader() }),
        axios.get(`${API_URL}/api/hr/employees/${userId}/assets`, { headers: authHeader() }).catch(() => ({ data: { assets: [] } })),
      ]);
      setData(d.data);
      setForm(d.data?.profile || {});
      setAssets(a.data?.assets || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load employee');
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const save = async () => {
    setBusy(true);
    try {
      // Drop nulls/empties so we don't overwrite previously-saved values with ""
      const payload = {};
      Object.keys(form).forEach(k => { if (form[k] != null && form[k] !== '') payload[k] = form[k]; });
      await axios.put(`${API_URL}/api/hr/employees/${userId}`, payload, { headers: authHeader() });
      toast.success('Profile saved');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2_500_000) { toast.error('Photo too large (max 2.5MB)'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await axios.put(`${API_URL}/api/hr/employees/${userId}/photo`,
          { photo_base64: String(reader.result) }, { headers: authHeader() });
        toast.success('Photo updated');
        load();
      } catch (err) { toast.error('Upload failed'); }
    };
    reader.readAsDataURL(file);
  };

  const downloadBadge = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/hr/employees/${userId}/badge.pdf`,
        { headers: authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `badge-${(data?.name || userId).replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not generate badge'); }
  };

  const loadAvailable = async (q = '') => {
    setAvailLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/hr/employees/${userId}/available-assets`, {
        headers: authHeader(),
        params: q ? { q } : {},
      });
      setAvailable(r.data?.assets || []);
    } catch (err) { toast.error('Failed to load available assets'); }
    finally { setAvailLoading(false); }
  };

  const openAssign = () => {
    setShowAssign(true);
    setAvailSearch('');
    loadAvailable('');
  };

  const assignAsset = async (assetId) => {
    try {
      await axios.post(`${API_URL}/api/hr/employees/${userId}/assets/${assetId}/assign`, {},
        { headers: authHeader() });
      toast.success('Asset assigned');
      setShowAssign(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Assign failed'); }
  };

  const unassignAsset = async (assetId) => {
    if (!window.confirm('Return this asset to stock?')) return;
    try {
      await axios.post(`${API_URL}/api/hr/employees/${userId}/assets/${assetId}/unassign`, {},
        { headers: authHeader() });
      toast.success('Asset returned to stock');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Unassign failed'); }
  };

  if (!data) return <div className="p-12 text-center text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>;

  const stripe = data.stripe_color;
  const photoUrl = data.photo_data_url;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const initials = (data.name || '?').split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase();
  const onboardingIncomplete = !photoUrl || !form.job_title || !form.department;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="hr-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Link to="/hr" className="text-sm text-[#0E7490] hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> All employees
        </Link>
        {onboardingIncomplete && (
          <Button size="sm" variant="outline" onClick={() => navigate(`/hr/${userId}/onboarding`)}
                  className="text-[#92400E] border-[#FCD34D] bg-[#FEF3C7]"
                  data-testid="start-onboarding-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Complete onboarding
          </Button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-3">
        {/* ID badge preview */}
        <Card className="p-0 overflow-hidden border-[#E2E8F0]" data-testid="badge-preview">
          <div className="h-2" style={{ background: stripe }} />
          <div className="p-5">
            <div className="flex items-start gap-4">
              {photoUrl ? (
                <img alt="" src={photoUrl}
                     className="w-24 h-24 rounded-md object-cover border border-[#E2E8F0]"
                     data-testid="badge-photo" />
              ) : (
                <div className="w-24 h-24 rounded-md bg-[#F1F5F9] flex items-center justify-center text-2xl font-bold text-[#64748B]"
                     data-testid="badge-photo-placeholder">
                  {initials}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wide font-semibold" style={{ color: stripe }}>
                  {data.role?.replace(/_/g, ' ')}
                </div>
                <h2 className="text-lg font-bold text-[#0A2342] mt-1">{data.name}</h2>
                <p className="text-xs text-[#64748B]">{data.profile?.job_title || data.role?.replace(/_/g, ' ')}</p>
                <p className="text-[10px] text-[#94A3B8] mt-1">{data.profile?.department || '—'}</p>
                <div className="mt-2 text-[10px] font-mono text-[#0A2342]">
                  ID: {data.profile?.employee_id_external || `EMP-${userId.slice(0, 6).toUpperCase()}`}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" onClick={() => fileRef.current?.click()} variant="outline" data-testid="upload-photo-btn">
                <Camera className="w-3.5 h-3.5 mr-1" /> Upload photo
              </Button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png" hidden onChange={handlePhoto} />
              <Button size="sm" onClick={downloadBadge}
                      className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white"
                      data-testid="download-badge-btn">
                <Download className="w-3.5 h-3.5 mr-1" /> Badge PDF
              </Button>
            </div>
          </div>
        </Card>

        {/* Profile fields */}
        <Card className="p-5 border-[#E2E8F0] lg:col-span-2">
          <h3 className="text-sm font-semibold text-[#0A2342] mb-3 flex items-center gap-2">
            <IdCard className="w-4 h-4 text-[#0E7490]" /> Employee profile
          </h3>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div><Label>Login email</Label><Input value={data.email || ''} disabled className="bg-[#F8FAFC]" data-testid="hr-login-email" /></div>
            <div><Label>Work email</Label><Input value={form.work_email || ''} onChange={e => set('work_email', e.target.value)} placeholder={data.email} data-testid="hr-work-email" /></div>
            <div><Label>Job title</Label><Input value={form.job_title || ''} onChange={e => set('job_title', e.target.value)} data-testid="hr-job-title" /></div>
            <div><Label>Department</Label><Input value={form.department || ''} onChange={e => set('department', e.target.value)} data-testid="hr-department" /></div>
            <div>
              <Label>Employment type</Label>
              <select value={form.employment_type || ''} onChange={e => set('employment_type', e.target.value)}
                      className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white text-sm"
                      data-testid="hr-employment-type">
                <option value="">—</option>
                <option value="full_time">Full-time</option>
                <option value="part_time">Part-time</option>
                <option value="contractor">Contractor</option>
                <option value="intern">Intern</option>
                <option value="per_diem">Per diem</option>
              </select>
            </div>
            <div><Label>Work location</Label><Input value={form.work_location || ''} onChange={e => set('work_location', e.target.value)} placeholder="On-site / Remote / Hybrid · City" /></div>
            <div><Label>Employee ID</Label><Input value={form.employee_id_external || ''} onChange={e => set('employee_id_external', e.target.value)} placeholder={`EMP-${userId.slice(0, 6).toUpperCase()}`} /></div>
            <div><Label>Date of birth</Label><Input type="date" value={form.date_of_birth || ''} onChange={e => set('date_of_birth', e.target.value)} /></div>
            <div><Label>Start date</Label><Input type="date" value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} /></div>
            <div><Label>End date (offboarding)</Label><Input type="date" value={form.end_date || ''} onChange={e => set('end_date', e.target.value)} /></div>
            <div><Label>Phone</Label><Input value={form.phone || ''} onChange={e => set('phone', e.target.value)} /></div>
            <div className="sm:col-span-2 mt-2 pt-3 border-t border-[#E2E8F0]">
              <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">Emergency contact</div>
            </div>
            <div><Label>Contact name</Label><Input value={form.emergency_contact_name || ''} onChange={e => set('emergency_contact_name', e.target.value)} /></div>
            <div><Label>Contact phone</Label><Input value={form.emergency_contact_phone || ''} onChange={e => set('emergency_contact_phone', e.target.value)} /></div>
            <div><Label>Relationship</Label><Input value={form.emergency_contact_relationship || ''} onChange={e => set('emergency_contact_relationship', e.target.value)} placeholder="Spouse / Parent / Friend" /></div>
            <div className="sm:col-span-2"><Label>Home address</Label><Input value={form.address || ''} onChange={e => set('address', e.target.value)} /></div>
            <div className="sm:col-span-2"><Label>Notes</Label>
              <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={2}
                        className="w-full mt-1 rounded-md border border-[#E2E8F0] px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <Button onClick={save} disabled={busy}
                    className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white"
                    data-testid="hr-save-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save
            </Button>
          </div>
        </Card>
      </div>

      {/* In-service training (aides only) */}
      {data.role === 'aide' && (
        <div className="mt-6">
          <InServiceTrainingCard userId={userId} variant="supervisor" />
        </div>
      )}

      {/* Assigned assets */}
      <Card className="mt-6 p-5 border-[#E2E8F0]" data-testid="employee-assets">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#0A2342] flex items-center gap-2">
            <Package className="w-4 h-4 text-[#0E7490]" /> Assigned hardware ({assets.length})
          </h3>
          <Button size="sm" onClick={openAssign}
                  className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white"
                  data-testid="assign-asset-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Assign asset
          </Button>
        </div>
        {assets.length === 0 ? (
          <p className="text-xs text-[#64748B]">No hardware currently assigned to this employee.</p>
        ) : (
          <div className="space-y-1">
            {assets.map(a => (
              <div key={a.id}
                   className="flex items-center justify-between text-xs border border-[#F1F5F9] rounded px-2 py-1.5 hover:bg-[#F8FAFC]"
                   data-testid={`assigned-asset-row-${a.id}`}>
                <Link to={`/assets/${a.id}`} className="flex-1 min-w-0">
                  <span className="font-mono text-[#0A2342]">{a.asset_tag}</span>
                  <span className="ml-2 text-[#64748B]">{a.name}</span>
                </Link>
                <span className="text-[10px] text-[#94A3B8] mr-3 hidden sm:inline">
                  {a.manufacturer} {a.model}
                </span>
                <button onClick={() => unassignAsset(a.id)}
                        className="text-[11px] text-[#9F1239] hover:underline inline-flex items-center gap-1"
                        data-testid={`unassign-asset-${a.id}`}>
                  <Undo2 className="w-3 h-3" /> Return
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Assign asset dialog */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="max-w-lg" data-testid="assign-asset-dialog">
          <DialogHeader>
            <DialogTitle>Assign hardware to {data.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#94A3B8]" />
              <Input
                value={availSearch}
                onChange={(e) => { setAvailSearch(e.target.value); loadAvailable(e.target.value); }}
                placeholder="Search asset tag, name, manufacturer, model…"
                className="pl-8"
                data-testid="asset-search-input"
              />
            </div>
            <div className="max-h-80 overflow-y-auto border border-[#E2E8F0] rounded">
              {availLoading ? (
                <div className="p-6 text-center text-xs text-[#64748B]">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
                </div>
              ) : available.length === 0 ? (
                <div className="p-6 text-center text-xs text-[#64748B]">
                  No available assets in your organization.
                </div>
              ) : (
                available.map(a => (
                  <button key={a.id} onClick={() => assignAsset(a.id)}
                          className="w-full text-left flex items-center justify-between px-3 py-2 text-xs border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]"
                          data-testid={`available-asset-${a.id}`}>
                    <div>
                      <span className="font-mono text-[#0A2342]">{a.asset_tag}</span>
                      <span className="ml-2 text-[#0A2342]">{a.name}</span>
                      <div className="text-[10px] text-[#94A3B8]">{a.manufacturer} {a.model}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wide text-[#0E7490]">{a.status}</span>
                  </button>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(false)} data-testid="assign-cancel-btn">
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
